// 📄 guiaService.ts
// Aprendizaje del proyecto (plan-proyectos-generales.md, G7, etapa 1): guías
// para los usuarios finales (cómo tomar un pedido, cómo cerrar caja...), con
// pasos (texto + imagen opcional), adjuntos y un video externo. El equipo las
// escribe; el rol cliente ve SOLO las publicadas (firestore.rules lo exige,
// por eso la consulta del cliente filtra por estado). La etapa 2 agregará un
// enlace de acceso para usuarios finales que no son miembros del proyecto.
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from "firebase/firestore";
import { db } from '../firebase';
import { uploadAttachment, UploadedAttachment } from './storageService.ts';

export type GuiaEstado = 'Borrador' | 'Publicada';

export interface PasoGuia {
  id: string;
  titulo: string;
  contenido: string;
  imagen?: UploadedAttachment | null;
}

export interface GuiaFormData {
  titulo: string;
  descripcion: string;
  categoria: string; // área del proyecto o texto libre ("" = general)
  videoUrl: string; // enlace externo (YouTube, Drive, Loom...)
  pasos: PasoGuia[];
  adjuntos: UploadedAttachment[];
}

export interface Guia extends GuiaFormData {
  id: string;
  estado: GuiaEstado;
  autorUid: string;
  autorNombre: string;
  creadoEn: any;
  actualizadoEn: any;
  actualizadoPorNombre: string;
}

export const LIMITES_GUIA = { pasos: 50, adjuntos: 10, bytesArchivo: 15 * 1024 * 1024 };

const guiasRef = (projectId: string) => collection(db, "projects", projectId, "guias");
const guiaRef = (projectId: string, id: string) => doc(db, "projects", projectId, "guias", id);

export const nuevoPaso = (): PasoGuia => ({ id: Math.random().toString(36).slice(2, 10), titulo: '', contenido: '', imagen: null });

/** El cliente solo puede leer las publicadas: la consulta tiene que pedirlo así (reglas). */
export const getGuias = async (projectId: string, soloPublicadas: boolean): Promise<Guia[]> => {
  const q = soloPublicadas ? query(guiasRef(projectId), where('estado', '==', 'Publicada')) : guiasRef(projectId);
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as Omit<Guia, "id">) }))
    .sort((a, b) => a.titulo.localeCompare(b.titulo));
};

const limpiar = (data: GuiaFormData): GuiaFormData => {
  if (!data.titulo.trim()) throw new Error("El título de la guía es obligatorio.");
  if (data.pasos.length > LIMITES_GUIA.pasos) throw new Error(`Una guía admite hasta ${LIMITES_GUIA.pasos} pasos.`);
  if (data.adjuntos.length > LIMITES_GUIA.adjuntos) throw new Error(`Una guía admite hasta ${LIMITES_GUIA.adjuntos} adjuntos.`);
  const video = data.videoUrl.trim();
  if (video && !/^https:\/\//i.test(video)) throw new Error("El enlace del video tiene que empezar con https://");
  return {
    titulo: data.titulo.trim(),
    descripcion: data.descripcion.trim(),
    categoria: data.categoria.trim(),
    videoUrl: video,
    pasos: data.pasos
      .filter((p) => p.titulo.trim() || p.contenido.trim() || p.imagen)
      .map((p) => ({ id: p.id, titulo: p.titulo.trim(), contenido: p.contenido.trim(), imagen: p.imagen || null })),
    adjuntos: data.adjuntos
  };
};

export const createGuia = async (projectId: string, data: GuiaFormData, uid: string, nombre: string): Promise<string> => {
  const ref = doc(guiasRef(projectId));
  await setDoc(ref, {
    ...limpiar(data),
    estado: 'Borrador' as GuiaEstado,
    autorUid: uid,
    autorNombre: nombre,
    actualizadoPorNombre: nombre,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp()
  });
  return ref.id;
};

export const updateGuia = async (projectId: string, id: string, data: GuiaFormData, nombre: string) => {
  await updateDoc(guiaRef(projectId, id), { ...limpiar(data), actualizadoPorNombre: nombre, actualizadoEn: serverTimestamp() });
};

export const setEstadoGuia = async (projectId: string, id: string, estado: GuiaEstado, nombre: string) => {
  await updateDoc(guiaRef(projectId, id), { estado, actualizadoPorNombre: nombre, actualizadoEn: serverTimestamp() });
};

export const deleteGuia = async (projectId: string, id: string) => {
  await deleteDoc(guiaRef(projectId, id));
};

/** Sube una imagen de paso o un adjunto a projects/{id}/guias/... (mismo storage que el resto del proyecto). */
export const subirArchivoGuia = async (projectId: string, file: File): Promise<UploadedAttachment> => {
  if (file.size > LIMITES_GUIA.bytesArchivo) throw new Error("El archivo supera los 15 MB.");
  return uploadAttachment(`projects/${projectId}/guias`, file);
};

/** Enlace de YouTube → URL embebible (para verlo dentro de la guía). Otros enlaces se abren aparte. */
export const urlVideoEmbebible = (url: string): string | null => {
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/i);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};
